import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { GasOrderSummary } from '@tucupet/shared';
import { PageHeader, Table, Select, Field, Pager, Btn } from '../components/ui';
import { api } from '../lib/api';
import { GasStatusBadge } from './gas-shared';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending_payment', label: 'Por pagar' },
  { value: 'paid', label: 'Pagado' },
  { value: 'fulfilled', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'expired', label: 'Expirado' },
];

interface ListResp {
  items: GasOrderSummary[];
  total: number;
  page: number;
  limit: number;
}

export default function GasPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const search = sp.get('search') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data, isLoading } = useQuery<ListResp>({
    queryKey: ['admin', 'gas', status, search, page],
    queryFn: () => api('/api/admin/gas/orders', { query: { status: status || undefined, search: search || undefined, page } }),
  });

  const setFilter = (next: Partial<{ status: string; search: string; page: string }>) => {
    const cur = { status, search, page: String(page) };
    const merged: Record<string, string> = {};
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  return (
    <div>
      <PageHeader
        kicker="GAS / BALITAS"
        title={`Pedidos de gas · ${data?.total ?? 0}`}
        sub="Gestiona los pedidos de balitas: pagos, entrega a domicilio o recogida."
        action={<Btn variant="ghost" onClick={() => nav('/gas/catalogo')}>Catálogo</Btn>}
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <Select label="Estado" value={status} onChange={(v) => setFilter({ status: v, page: '1' })} options={STATUS_OPTIONS} />
        </div>
        <div style={{ minWidth: 260, flex: 1 }}>
          <Field label="Buscar" value={search} onChange={(v) => setFilter({ search: v, page: '1' })} placeholder="Código, teléfono o dirección" />
        </div>
      </div>

      <Table<GasOrderSummary>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin pedidos de gas'}
        columns={[
          {
            key: 'code',
            label: 'Código',
            render: (r) => (
              <Link to={`/gas/${r.code}`} className="mono" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                {r.code}
              </Link>
            ),
          },
          {
            key: 'entrega',
            label: 'Entrega',
            render: (r) =>
              r.deliveryMode === 'pickup' ? (
                <span>Recogida{r.pickupPointName ? ` · ${r.pickupPointName}` : ''}</span>
              ) : (
                <span>Domicilio{r.deliveryMunicipality ? ` · ${r.deliveryMunicipality}` : ''}</span>
              ),
          },
          { key: 'phone', label: 'Contacto', render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.deliveryPhone ?? '—'}</span> },
          { key: 'items', label: 'Artículos', render: (r) => <span className="mono">{r.items.reduce((n, l) => n + l.quantity, 0)}</span> },
          { key: 'total', label: 'Total', render: (r) => <span className="mono" style={{ fontWeight: 600 }}>${r.total.toFixed(2)}</span> },
          { key: 'status', label: 'Estado', render: (r) => <GasStatusBadge status={r.status} /> },
          { key: 'date', label: 'Fecha', render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(r.createdAt).toLocaleString('es-MX')}</span> },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={(p) => setFilter({ page: String(p) })} />}
    </div>
  );
}
